import {
  useQuery,
  UseQueryResult,
  useQueryClient,
  UseMutationResult,
  useMutation,
} from '@tanstack/react-query';

import {
  LocationNodeData,
  PaginationSettings,
  StructureResponse,
  SiteSchemaDropdown,
  SiblingParentDetails,
  NodeAttributesReponse,
  AddressAttributeResponseType,
  NodeData,
  DispatchDestinationNodeType,
  FaultCode,
} from '../@types';
import { StructureMergeDecisionData } from '../@types/live-search.type';
import { SiteSchemaData } from '../@types/site-schema';
import { PrioritySelectionObjectType } from '../@types/structure.type';
import { Folders } from '../@types/uploaded-files.type';
import {
  getStructuresByTypeCategoryCode,
  getStructuresByTypeCategoryCodeAndTypeCode,
  createStructure,
  getNodeChildrenByNodeType,
  getNodeAttributesByCustomRange,
  getNodeById,
  updateStructure,
  getNodeChildren,
  updateStructureBasicData,
  getNodeSubTree,
  getNodeFilteredAttributesByCustomRange,
  getParentAttributesByPropertyType,
  getPrioritySelection,
  getSuggestgedNodesFromAllocationEngine,
  getSiteSchemasByClientContract,
  getAllPartsForStructures,
  getNodeByName,
  getAllSiteSchemas,
  postSiteSchema,
  updateSiteSchema,
  getSiteSchema,
  getLoggedInSiblingParentDetails,
  getAllShipmentsForStructures,
  getDestinationAddressByNodeId,
  getNodeAttributesByPropertyTypeAndNodeIds,
  getNodeAttributesByNodeId,
  getAllNodesByNodeType,
  mergeStructure,
  getDispatchDestinationSuggestions,
  getNodeAttributeById,
  getAllocationNodeParentData,
  getCommonFleetTagsByPartTypeIds,
  checkMultipleServiceCreationAccess,
  childLocationCountGroupedByNodeType,
  StructureCreationResponse,
  StructureListResponse,
  PrioritySelectionResponse,
  MultipleServiceCreationAccess,
  ChildLocationCount,
  FleetTagItem,
  StructureQueryOptions,
} from '../api/structures';
import { uploadFiles } from '../api/uploadedFiles';
import { ColumnSelectionItem } from '../components/templates/ColumnSelection';
import {
  FilteredNodeAttributesByCustomRangeKey,
  NodeAttributesByCustomRangeKey,
  NodeByName,
  ParentAttributesByPropertyTypeKey,
  PrioritySelectionObjectTypeKey,
  SiteSchemaAllKey,
  SiteSchemaOneKey,
  StructureChildrenKey,
  StructureKey,
  StructureSubTreeKey,
  StructureSiteSchemaKey,
  PartsForStructuresKey,
  SiblingParentDetailsKey,
  ShipmentsForStructuresKey,
  NodeAttributesByPropertyTypeAndNodeIdsKey,
  NodeAttributesByNodeIdKey,
  NodeByTypeKey,
  NodesAllocationByNode,
  NodesAllocationByService,
  ShipmentDestinationNodesKey,
  NodeAttributeByIdKey,
  NodeAllocationParentByIdKey,
  MultipleServiceCreationKey,
  ChildLocationCountKey,
} from '../configs/structures';
import { StructurePartListType } from '../pages/Structures/AddEditStructureLocation/PartsAccordianFragment';
import { StructureShipmentListType } from '../pages/Structures/AddEditStructureLocation/ShipmentAccordianFragment';
import { getSelectedColumnNames } from '../utils/columnSelectionOperations';

// Re-export StructureListResponse from API
export type { StructureListResponse } from '../api/structures';

export interface LogoPicUploadData {
  id?: string | number;
  logoPic?: string | FileList;
  parentNodeId?: string | number | null;
  nodeTypeCode?: string;
  name?: string;
  code?: string;
  isActive?: boolean;
  attributes?: unknown[];
  [key: string]: unknown;
}

export const useGetStructurePaginatedDataByType = (
  catagoryCode: string,
  queryOption: StructureQueryOptions,
  columnSelectionItems?: ColumnSelectionItem[],
  defaultExcludedColumns: string[] = []
): UseQueryResult<StructureListResponse> => {
  return useQuery({
    queryKey: [StructureKey, queryOption],
    queryFn: ({ signal }) =>
      getStructuresByTypeCategoryCode(
        catagoryCode,
        {
          ...queryOption,
          excludedColumns: getSelectedColumnNames(columnSelectionItems, defaultExcludedColumns),
        },
        signal
      ),
    gcTime: 0,
    enabled: false,
  });
};

export const useGetStructuresByTypeCategoryCodeAndTypeCode = (
  catagoryCode: string,
  typeCode: string
): UseQueryResult<StructureResponse[]> => {
  return useQuery({
    queryKey: [StructureKey, catagoryCode, typeCode],
    queryFn: ({ signal }) =>
      getStructuresByTypeCategoryCodeAndTypeCode(catagoryCode, typeCode, signal),
    enabled: false,
  });
};

export const useGetStructuresByTypeCategoryCodeAndTypeCodeForReplenishment = (
  catagoryCode: string,
  typeCode: string
): UseQueryResult<StructureResponse[]> => {
  return useQuery({
    queryKey: [StructureKey, catagoryCode, typeCode],
    queryFn: ({ signal }) =>
      getStructuresByTypeCategoryCodeAndTypeCode(catagoryCode, typeCode, signal),
  });
};

export const useGetNodeChildrenByNodeType = (
  nodeId: string,
  nodeTypeCode: string
): UseQueryResult<StructureResponse[]> => {
  return useQuery({
    queryKey: [StructureChildrenKey, nodeId, nodeTypeCode],
    queryFn: ({ signal }) => getNodeChildrenByNodeType(nodeId, nodeTypeCode, signal),
    enabled: !!nodeId && nodeId !== '0',
  });
};

// Interface for node children by type query
interface NodeChildrenByTypeData {
  nodeId: string;
  nodeTypeCode: string;
}

export const useGetNodeChildrenByNodeTypeAsEnabled = (): UseMutationResult<
  StructureResponse[],
  Error,
  NodeChildrenByTypeData
> => {
  return useMutation({
    mutationFn: async (data: NodeChildrenByTypeData) => {
      return await getNodeChildrenByNodeType(data.nodeId, data.nodeTypeCode);
    },
  });
};

export const useGetNodeChildren = (
  nodeId: string,
  includeInactiveChildren = false
): UseQueryResult<StructureResponse[]> => {
  return useQuery({
    queryKey: [StructureChildrenKey, nodeId],
    queryFn: ({ signal }) => getNodeChildren(nodeId, includeInactiveChildren, signal),
    enabled: false,
  });
};

export const useCreateStructure = (): UseMutationResult<
  StructureCreationResponse,
  Error,
  LogoPicUploadData
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: LogoPicUploadData) => {
      const createResponse = await createStructure(data as unknown as Record<string, unknown>);

      if (createResponse && createResponse.id && data.logoPic) {
        const formData = new FormData();
        const files = data.logoPic;
        formData.append('fileList', files[0]);
        await uploadFiles(createResponse.id, formData, Folders.ClientContractLogos);
      }

      return createResponse;
    },

    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: [StructureKey],
      });
      await queryClient.invalidateQueries({
        queryKey: [StructureChildrenKey, `${response.parentNodeId}`, `${response.nodeTypeCode}`],
      });
    },
  });
};

/* eslint-disable @typescript-eslint/no-explicit-any -- pre-existing; hook signatures in this block still carry `any` for nodeAttributes / UseQueryResult. Tightening them is a separate cleanup. */
export const useGetFilteredNodeAttributesByCustomRange = (
  nodeId: string,
  nodeAttributes: any,
  serviceTypeCode: string,
  faultType?: string,
  actionTypeCode?: string
): UseQueryResult<any> => {
  return useQuery({
    queryKey: [
      FilteredNodeAttributesByCustomRangeKey,
      nodeId,
      nodeAttributes,
      serviceTypeCode,
      actionTypeCode,
    ],

    queryFn: ({ signal }) =>
      getNodeFilteredAttributesByCustomRange(
        nodeId,
        nodeAttributes,
        serviceTypeCode,
        faultType,
        actionTypeCode,
        signal
      ),

    enabled: false,
  });
};

export const useGetNodeAttributesByCustomRange = (
  nodeId: string,
  nodeAttributes: any,
  visibilityBypassServiceId?: number
): UseQueryResult<any> => {
  return useQuery({
    queryKey: [NodeAttributesByCustomRangeKey, nodeId, nodeAttributes, visibilityBypassServiceId],
    queryFn: ({ signal }) =>
      getNodeAttributesByCustomRange(nodeId, nodeAttributes, visibilityBypassServiceId, signal),
    enabled: nodeId !== '0' && nodeId !== '',
  });
};

export const useGetNodeAttributesByCustomRangeForReplenishment = (
  nodeId: string,
  nodeAttributes: any
): UseQueryResult<any> => {
  return useQuery({
    queryKey: [NodeAttributesByCustomRangeKey, nodeId, nodeAttributes],
    queryFn: ({ signal }) =>
      getNodeAttributesByCustomRange(nodeId, nodeAttributes, undefined, signal),
  });
};

export const useGetNodeByName = (
  name: string,
  nodeTypeCode: string,
  parentNodeId?: number
): UseQueryResult<any> => {
  return useQuery({
    queryKey: [NodeByName, name, nodeTypeCode, parentNodeId || 0],
    queryFn: ({ signal }) => getNodeByName(name, nodeTypeCode, parentNodeId, signal),
    enabled: !!name && !!nodeTypeCode,
  });
};
/* eslint-enable @typescript-eslint/no-explicit-any */

export const useUpdateStructure = (): UseMutationResult<
  StructureCreationResponse,
  Error,
  LogoPicUploadData
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: LogoPicUploadData) => {
      if (data.logoPic) {
        const formData = new FormData();
        const files = data.logoPic;
        formData.append('fileList', files[0]);
        await uploadFiles(Number(data.id), formData, Folders.ClientContractLogos);
      }

      return await updateStructure(String(data.id), data as unknown as Record<string, unknown>);
    },

    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: [StructureKey],
      });
      await queryClient.invalidateQueries({
        queryKey: [StructureKey, `${data.id}`],
      });
      await queryClient.invalidateQueries({
        queryKey: [StructureSubTreeKey, `${data.id}`],
      });
      await queryClient.invalidateQueries({
        queryKey: [StructureChildrenKey, `${data.parentNodeId}`, `${data.nodeTypeCode}`],
      });
    },
  });
};

// Interface for structure basic data update
interface StructureBasicUpdateData {
  id: number | string;
  [key: string]: unknown;
}

export const useUpdateStructureBasicData = (): UseMutationResult<
  StructureResponse,
  Error,
  StructureBasicUpdateData
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: StructureBasicUpdateData) => {
      return await updateStructureBasicData(String(data.id), data as Record<string, unknown>);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [StructureKey],
      });
    },
  });
};

export const useGetNodeById = (
  nodeId: string,
  nodeTypeCategoryCode?: string,
  enabled: boolean = true
): UseQueryResult<StructureResponse> => {
  return useQuery({
    queryKey: [StructureKey, nodeId],
    queryFn: ({ signal }) => getNodeById(nodeId, nodeTypeCategoryCode, signal),
    enabled: enabled && nodeId !== 'create',
  });
};

export const useGetNodeSubTree = (
  nodeId: string
): UseQueryResult<StructureResponse[] | undefined> => {
  return useQuery<StructureResponse[] | undefined, Error>({
    queryKey: [StructureSubTreeKey, nodeId],
    queryFn: ({ signal }) => getNodeSubTree(nodeId, signal),
    enabled: nodeId !== '0',
  });
};

export const useGetParentAttributesByPropertyType = (
  nodeId: string,
  propertyTypeCode: string,
  visibilityBypassServiceId?: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing; typing this is a separate cleanup.
): UseQueryResult<any[]> => {
  return useQuery({
    queryKey: [ParentAttributesByPropertyTypeKey, nodeId, propertyTypeCode],
    queryFn: ({ signal }) =>
      getParentAttributesByPropertyType(
        nodeId,
        propertyTypeCode,
        visibilityBypassServiceId,
        signal
      ),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: false,
  });
};

export const useGetPrioritySelection = (): UseMutationResult<
  PrioritySelectionResponse,
  Error,
  PrioritySelectionObjectType
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: PrioritySelectionObjectType) => {
      return await getPrioritySelection(data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PrioritySelectionObjectTypeKey],
      });
    },
  });
};

export const useGetSuggestedNodesFromAllocationEngine = (
  serviceTypeCode: string,
  actionTypeCode: string,
  processTypeCode: string,
  brandId?: number,
  mainLocationId?: number,
  serviceId?: number
): UseQueryResult<LocationNodeData[]> => {
  return useQuery({
    queryKey: serviceId
      ? [NodesAllocationByService, serviceId]
      : [NodesAllocationByNode, serviceTypeCode, actionTypeCode, processTypeCode, mainLocationId],
    queryFn: ({ signal }) =>
      getSuggestgedNodesFromAllocationEngine(
        serviceTypeCode,
        actionTypeCode,
        processTypeCode,
        brandId,
        mainLocationId,
        serviceId,
        signal
      ),
    enabled: false,
  });
};

export const useGetSiteSchemasByClientContract = (
  clientId: string,
  contractId: string
): UseQueryResult<SiteSchemaDropdown[]> => {
  return useQuery({
    queryKey: [StructureSiteSchemaKey, contractId],
    queryFn: ({ signal }) => getSiteSchemasByClientContract(clientId, contractId, signal),
    enabled: false,
  });
};

export const useGetAllPartsForStructures = (
  pageSettings: PaginationSettings,
  nodeId: number,
  isEnableToShowChildrenParts: boolean
): UseQueryResult<StructurePartListType> => {
  return useQuery({
    queryKey: [
      PartsForStructuresKey,
      pageSettings.currentPage,
      pageSettings.limit,
      nodeId,
      isEnableToShowChildrenParts,
    ],
    queryFn: ({ signal }) =>
      getAllPartsForStructures(
        pageSettings.limit,
        pageSettings.currentPage,
        nodeId,
        isEnableToShowChildrenParts,
        signal
      ),
    gcTime: 0,
  });
};

export const useGetAllSiteSchemas = (nodeId: number) => {
  return useQuery({
    queryKey: [SiteSchemaAllKey, nodeId],
    queryFn: ({ signal }) => getAllSiteSchemas(nodeId, signal),
  });
};

export const useGetSiteSchema = (siteSchemaId: number) => {
  return useQuery({
    queryKey: [SiteSchemaOneKey, siteSchemaId],
    queryFn: ({ signal }) => getSiteSchema(siteSchemaId, signal),
  });
};

export const useCreateSiteSchema = (
  nodeId: number
): UseMutationResult<SiteSchemaData, Error, SiteSchemaData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SiteSchemaData) => {
      return await postSiteSchema(data);
    },

    onSuccess: async (response: SiteSchemaData) => {
      await queryClient.invalidateQueries({
        queryKey: [SiteSchemaOneKey, response.id],
      });
      await queryClient.invalidateQueries({
        queryKey: [SiteSchemaAllKey, nodeId],
      });
    },
  });
};

export const useUpdateSiteSchema = (
  nodeId: number
): UseMutationResult<SiteSchemaData, Error, SiteSchemaData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SiteSchemaData) => {
      return await updateSiteSchema(data);
    },

    onSuccess: async (response: SiteSchemaData) => {
      await queryClient.invalidateQueries({
        queryKey: [SiteSchemaOneKey, response.id],
      });
      await queryClient.invalidateQueries({
        queryKey: [SiteSchemaAllKey, nodeId],
      });
    },
  });
};

export const useGetLoggedInSiblingParentDetails = (
  returnImmediateParentNode?: boolean
): UseQueryResult<SiblingParentDetails> => {
  return useQuery({
    queryKey: [SiblingParentDetailsKey],
    queryFn: ({ signal }) => getLoggedInSiblingParentDetails(returnImmediateParentNode, signal),
    enabled: false,
  });
};

export const useGetAllShipmentsForStructures = (
  pageSettings: PaginationSettings,
  nodeId: number,
  isEnableToShowChildrenShipments: boolean
): UseQueryResult<StructureShipmentListType> => {
  return useQuery({
    queryKey: [
      ShipmentsForStructuresKey,
      pageSettings.currentPage,
      pageSettings.limit,
      nodeId,
      isEnableToShowChildrenShipments,
    ],
    queryFn: ({ signal }) =>
      getAllShipmentsForStructures(
        pageSettings.limit,
        pageSettings.currentPage,
        nodeId,
        isEnableToShowChildrenShipments,
        signal
      ),
    gcTime: 0,
  });
};

export const useGetDestinationAddressByNodeId = (
  nodeId: string
): UseQueryResult<AddressAttributeResponseType[]> => {
  return useQuery({
    queryKey: [ShipmentsForStructuresKey, nodeId],
    queryFn: ({ signal }) => getDestinationAddressByNodeId(nodeId, signal),
    enabled: false,
  });
};

export const useGetNodeAttributesByPropertyTypeAndNodeIds = (
  propertyTypeCode: string,
  nodeIds: string
): UseQueryResult<NodeAttributesReponse[]> => {
  return useQuery({
    queryKey: [NodeAttributesByPropertyTypeAndNodeIdsKey, propertyTypeCode, nodeIds],
    queryFn: ({ signal }) =>
      getNodeAttributesByPropertyTypeAndNodeIds(propertyTypeCode, nodeIds, signal),
  });
};

export const useGetNodeAttributesByNodeId = (
  nodeId: number
): UseQueryResult<NodeAttributesReponse[]> => {
  return useQuery({
    queryKey: [NodeAttributesByNodeIdKey, nodeId],
    queryFn: ({ signal }) => getNodeAttributesByNodeId(nodeId, signal),
    enabled: !!nodeId,
  });
};

export const useGetAllNodesByNodeType = (nodeTypeCode: string): UseQueryResult<NodeData[]> => {
  return useQuery({
    queryKey: [NodeByTypeKey, nodeTypeCode],
    queryFn: ({ signal }) => getAllNodesByNodeType(nodeTypeCode, signal),
  });
};

export const useMergeStructure = (): UseMutationResult<
  StructureResponse,
  Error,
  StructureMergeDecisionData
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: StructureMergeDecisionData) => {
      return await mergeStructure(data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [StructureKey],
      });
    },
  });
};

export const useGetDispatchDestinationSuggestions = (
  serviceId: number,
  mainLocationNodeId: number,
  subLocationNodeId: number,
  primaryAllocationNodeId: number,
  secondaryAllocationNodeId: number
): UseQueryResult<DispatchDestinationNodeType[]> => {
  return useQuery({
    // The response varies by every param, not just serviceId — e.g. the same
    // job re-allocated to another technician must not reuse the old list.
    queryKey: [
      ShipmentDestinationNodesKey,
      serviceId,
      mainLocationNodeId,
      subLocationNodeId,
      primaryAllocationNodeId,
      secondaryAllocationNodeId,
    ],
    queryFn: ({ signal }) =>
      getDispatchDestinationSuggestions(
        serviceId,
        mainLocationNodeId,
        subLocationNodeId,
        primaryAllocationNodeId,
        secondaryAllocationNodeId,
        signal
      ),
    enabled: false,
  });
};

export const useGetNodeAttributeById = (
  nodeAttributeId: number
): UseQueryResult<FaultCode | undefined> => {
  return useQuery<FaultCode | undefined, Error>({
    queryKey: [NodeAttributeByIdKey, nodeAttributeId],
    queryFn: ({ signal }) => getNodeAttributeById(nodeAttributeId, signal),
    enabled: nodeAttributeId !== -1,
  });
};

export const useGetAllocationNodeParentData = (
  nodeId?: number
): UseQueryResult<NodeData | undefined> => {
  return useQuery<NodeData | undefined, Error>({
    queryKey: [NodeAllocationParentByIdKey, nodeId],
    queryFn: ({ signal }) => getAllocationNodeParentData(nodeId!, signal),
    enabled: !!nodeId,
  });
};

export const useGetCommonFleetTagsByPartTypeIds = (): UseMutationResult<
  FleetTagItem[],
  Error,
  { partTypeIds: number[] }
> => {
  return useMutation({
    mutationFn: async (data: { partTypeIds: number[] }) => {
      return await getCommonFleetTagsByPartTypeIds(data.partTypeIds);
    },
  });
};

export const useCheckMultipleServiceCreationAccess = (
  nodeId: string
): UseQueryResult<MultipleServiceCreationAccess> => {
  return useQuery({
    queryKey: [MultipleServiceCreationKey, nodeId],
    queryFn: ({ signal }) => checkMultipleServiceCreationAccess(nodeId, signal),
    enabled: false,
  });
};

export const useChildLocationCountGroupedByNodeType = (
  locationId: string
): UseQueryResult<ChildLocationCount | undefined> => {
  return useQuery({
    queryKey: [ChildLocationCountKey, locationId],
    queryFn: ({ signal }) => childLocationCountGroupedByNodeType(locationId, signal),
    enabled: false,
  });
};
